import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import BackLink from '@components/BackLink';
import BottomBar from '@components/BottomBar';
import {
  FormDateField,
  FormOptionsField,
  FormOptionValue,
  FormSliderField,
  FormTextField,
} from '@components/Form';
import FormFileField from '@components/Form/FormFileField';
import FormSelectField from '@components/Form/FormSelectField';
import { useInjection } from '@components/Injection';
import Layout from '@components/Layout';
import { Wizard, WizardNextButton, WizardPreviousButton, WizardStep, WizardSteps } from '@components/Wizard';
import { RoutePath } from '@constants/route-path';
import { BraceOrderStatus } from '@modulate/shared/src/constants/brace-order-status';
import { BraceType } from '@modulate/shared/src/constants/brace-type';
import { OpeningType } from '@modulate/shared/src/constants/opening-type';
import { braceOrderValidationSchema, IBraceOrder } from '@modulate/shared/src/models/brace-order';
import { IPatient } from '@modulate/shared/src/models/patient';
import { useAuth } from '@src/contexts/authContext';
import {
  BRACE_ORDERS_SERVICE,
  CLINICS_SERVICE,
  FILES_SERVICE,
  IBraceOrdersService,
  IClinicsService,
  IFilesService,
  IPatientsService,
  PATIENTS_SERVICE,
} from '@src/services';

const initialValues: IBraceOrder = {
  status: BraceOrderStatus.Pending,
  clinicName: '',
  prescriberName: '',
  patientInfo: { patientId: null, height: '' as any, weight: '' as any },
  braceInfo: {
    name: '',
    date: new Date(),
    hoursPerDay: 0,
    opening: '',
    type: '',
  },
  prescriptionFiles: [],
  radiographyFiles: [],
  scansFiles: [],
};

function NewBraceOrderPage() {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const authContext = useAuth();

  const braceOrdersService = useInjection<IBraceOrdersService>(BRACE_ORDERS_SERVICE);
  const patientsService = useInjection<IPatientsService>(PATIENTS_SERVICE);
  const filesService = useInjection<IFilesService>(FILES_SERVICE);
  const clinicsService = useInjection<IClinicsService>(CLINICS_SERVICE);

  const [patient, setPatient] = useState<IPatient>();
  const [ordersCount, setOrdersCount] = useState<number>();
  const [notScanProvider, setNotScanProvider] = useState<boolean>(false);
  const [clinicName, setClinicName] = useState<string>();

  const NotScanProviderCheckbox = ({
    checked,
    onChange,
  }: {
    checked: boolean;
    onChange: (v: boolean) => void;
  }) => {
    const { validateForm } = useFormikContext<any>();
    return (
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <input
          id="notScanProviderCheckbox"
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            onChange(e.target.checked);
            validateForm();
          }}
          style={{ marginRight: 8 }}
        />
        <label htmlFor="notScanProviderCheckbox">I am not the scan provider</label>
      </div>
    );
  };

  useEffect(() => {
    patientsService.getById(patientId, authContext.userObject.clinicId).then(setPatient);
    braceOrdersService.getCountForPatient(patientId, authContext.userObject.clinicId).then(setOrdersCount);
    clinicsService.getById(authContext.userObject.clinicId).then((c) => setClinicName(c.name));
  }, [patientId]);

  const createOrder = async (order: IBraceOrder) => {
    await braceOrdersService.create(order);
    navigate(patientPath);
  };

  if (!patient || ordersCount === undefined || !clinicName) {
    return null;
  }

  const patientPath = generatePath(RoutePath.SinglePatientPage, { patientId });
  return (
    <Layout noFooter>
      <BackLink to={patientPath}>
        {patient.firstName} {patient.lastName}
      </BackLink>
      <h1 className="modTitle modTitle--h1">{t('newBrace.title', { name: patient.firstName })}</h1>

      <Wizard
        initialValues={{
          ...initialValues,
          patientInfo: { ...initialValues.patientInfo, patientId: patient.id },
          braceInfo: {
            ...initialValues.braceInfo,
            name: t('newBrace.braceDefaultName', { braceNumber: ordersCount + 1 }),
          },
          clinicName: clinicName,
        }}
        onComplete={createOrder}>
        <WizardSteps>
          <WizardStep
            title={t('newBrace.prescription.title')}
            validationSchema={braceOrderValidationSchema.pick([
              'prescriberName',
              'clinicName',
              'patientInfo',
              'prescriptionFiles',
            ])}>
            <FormTextField
              name="prescriberName"
              label={t('models.brace.prescriberName')}
              placeholder="John Doe"
            />
            <FormSelectField name="clinicName" label={t('models.brace.clinicName')}>
              <option value={clinicName}>{clinicName}</option>
            </FormSelectField>
            <FormTextField
              name="patientInfo.height"
              label={`${t('models.brace.patientInfo.height')} (cm)`}
              placeholder="140"
            />
            <FormTextField
              name="patientInfo.weight"
              label={`${t('models.brace.patientInfo.weight')} (kg)`}
              placeholder="50"
            />
            <FormFileField
              name="prescriptionFiles"
              uploadFile={filesService.uploadFile}
              maxFiles={2}
              accept={{ 'application/pdf': ['.pdf'], 'image/jpeg': ['.jpeg', '.jpg'] }}
              size={100}>
              {t('newBrace.prescription.fileFormats')}
            </FormFileField>
          </WizardStep>

          <WizardStep
            title={t('newBrace.radiography.title')}
            stepName={t('newBrace.radiography.stepName')}
            validationSchema={braceOrderValidationSchema.pick(['radiographyFiles'])}>
            <FormFileField
              key="radiographyFiles"
              name="radiographyFiles"
              uploadFile={filesService.uploadFile}
              maxFiles={10}
              accept={{ 'application/dicom': ['.dcm'], 'image/jpeg': ['.jpeg', '.jpg'] }}
              size={100}>
              <Trans i18nKey="newBrace.radiography.fileFormats" components={{ br: <br /> }} />
            </FormFileField>
          </WizardStep>

          <WizardStep
            title={t('newBrace.3dScan.title')}
            stepName={t('newBrace.3dScan.stepName')}
            validationSchema={Yup.object({})}>
            <NotScanProviderCheckbox checked={notScanProvider} onChange={setNotScanProvider} />
            <FormFileField
              key="scansFiles"
              name="scansFiles"
              uploadFile={filesService.uploadFile}
              maxFiles={10}
              accept={{ 'model/stl': ['.stl'], 'model/obj': ['.obj'] }}
              size={100}>
              {t('newBrace.3dScan.fileFormats')}
            </FormFileField>
          </WizardStep>

          <WizardStep
            title={t('newBrace.braceInfo.title')}
            validationSchema={braceOrderValidationSchema.pick(['braceInfo'])}>
            <FormTextField
              name="braceInfo.name"
              label={t('models.brace.braceInfo.name')}
              placeholder="Brace 001"
            />
            <FormDateField name="braceInfo.date" label={t('models.brace.braceInfo.date')} />
            <FormOptionsField name="braceInfo.type" label={t('models.brace.braceInfo.type')} size={100}>
              <FormOptionValue value={BraceType.FullTime}>
                <i className="modIcon modIcon--light-mode-brightness" />{' '}
                {t('enums.braceType', { context: BraceType.FullTime })}
              </FormOptionValue>
              <FormOptionValue value={BraceType.NightTime}>
                <i className="modIcon modIcon--weather-night-clear" />
                {t('enums.braceType', { context: BraceType.NightTime })}
              </FormOptionValue>
            </FormOptionsField>
            <FormSliderField
              name="braceInfo.hoursPerDay"
              label={t('models.brace.braceInfo.hoursPerDay')}
              min={0}
              max={24}
              formatValue={(value) => `${value}h`}
            />
            <FormOptionsField name="braceInfo.opening" label={t('models.brace.braceInfo.opening')} size={100}>
              <FormOptionValue value={OpeningType.Frontal}>
                <i className="modIcon modIcon--layers-select-front" />
                {t('enums.openingType', { context: OpeningType.Frontal })}
              </FormOptionValue>
              <FormOptionValue value={OpeningType.Dorsal}>
                <i className="modIcon modIcon--layers-select" />
                {t('enums.openingType', { context: OpeningType.Dorsal })}
              </FormOptionValue>
            </FormOptionsField>
          </WizardStep>
        </WizardSteps>

        <BottomBar>
          <WizardNextButton
            isDisabledOverride={({ stepIndex, isInitialized, formik }) => {
              if (!isInitialized) return true;
              const isScanStep = stepIndex === 2; // 0: prescription, 1: radiography, 2: 3D scan, 3: brace info
              if (!isScanStep) return undefined; // fall back to form validation for other steps
              const files = (formik.values as any)?.scansFiles as any[] | undefined;
              // Logic: if checked (notScanProvider) allow next without files; if unchecked, require at least one
              if (notScanProvider) {
                return false;
              }
              return !(Array.isArray(files) && files.length > 0);
            }}>
            {(isLastStep) =>
              isLastStep ? (
                <>
                  <i className="modIcon modIcon--check" /> {t('general.confirm')}
                </>
              ) : (
                <>
                  {t('general.next')} <i className="modIcon modIcon--chevron-right" />
                </>
              )
            }
          </WizardNextButton>
          <WizardPreviousButton>
            <i className="modIcon modIcon--chevron-left" /> {t('general.previous')}
          </WizardPreviousButton>
        </BottomBar>
      </Wizard>
    </Layout>
  );
}

export default NewBraceOrderPage;
